import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const StatusIndicatorBar = () => {
  const [processingStatus, setProcessingStatus] = useState({
    type: null,
    message: '',
    progress: 0,
    details: ''
  });
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const handleStatusUpdate = (event) => {
      setProcessingStatus(event?.detail);
    };

    window.addEventListener('statusUpdate', handleStatusUpdate);
    return () => window.removeEventListener('statusUpdate', handleStatusUpdate);
  }, []);

  if (!processingStatus?.type) return null;

  const statusConfig = {
    processing: {
      icon: 'Loader2',
      bgColor: 'bg-primary/10',
      textColor: 'text-primary',
      borderColor: 'border-primary/20'
    },
    success: {
      icon: 'CheckCircle2',
      bgColor: 'bg-success/10',
      textColor: 'text-success',
      borderColor: 'border-success/20'
    },
    warning: {
      icon: 'AlertTriangle',
      bgColor: 'bg-warning/10',
      textColor: 'text-warning',
      borderColor: 'border-warning/20'
    },
    error: {
      icon: 'XCircle',
      bgColor: 'bg-error/10',
      textColor: 'text-error',
      borderColor: 'border-error/20'
    }
  };

  const config = statusConfig?.[processingStatus?.type] || statusConfig?.processing;

  return (
    <div className="fixed top-16 left-0 right-0 z-[90] bg-card border-b border-border shadow-brand-sm">
      <div className="px-4 lg:px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`flex items-center justify-center w-8 h-8 rounded-md ${config?.bgColor}`}>
              <Icon 
                name={config?.icon} 
                size={18} 
                color={`var(--color-${processingStatus?.type})`}
                className={processingStatus?.type === 'processing' ? 'animate-spin' : ''}
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className={`font-medium ${config?.textColor} truncate`}>
                  {processingStatus?.message}
                </p>
                {processingStatus?.progress > 0 && (
                  <span className="caption text-muted-foreground whitespace-nowrap">
                    {processingStatus?.progress}%
                  </span>
                )}
              </div>
              
              {processingStatus?.progress > 0 && (
                <div className="mt-1.5 w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${config?.bgColor?.replace('/10', '')} transition-smooth`}
                    style={{ width: `${processingStatus?.progress}%` }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {processingStatus?.details && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="lg:hidden flex items-center justify-center w-8 h-8 rounded-md bg-muted text-foreground transition-smooth hover:bg-muted/80 active:scale-95"
                aria-label={isExpanded ? 'Ocultar detalhes' : 'Mostrar detalhes'}
              >
                <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={18} />
              </button>
            )}
            
            <button
              onClick={() => setProcessingStatus({ type: null, message: '', progress: 0, details: '' })}
              className="flex items-center justify-center w-8 h-8 rounded-md bg-muted text-foreground transition-smooth hover:bg-muted/80 active:scale-95"
              aria-label="Fechar"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {processingStatus?.details && (
          <>
            <div className="hidden lg:block mt-2 pt-2 border-t border-border">
              <p className="caption text-muted-foreground">
                {processingStatus?.details}
              </p>
            </div>
            
            {isExpanded && (
              <div className="lg:hidden mt-2 pt-2 border-t border-border">
                <p className="caption text-muted-foreground">
                  {processingStatus?.details}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StatusIndicatorBar;